/* ==========================================================================
   FLC Forge — Slider / Carousel
   Vanilla JS slider using scroll-snap with true infinite loop
   ========================================================================== */

class FLCSlider extends HTMLElement {
  connectedCallback() {
    this.track       = this.querySelector('[data-slider-track]');
    this.prevBtn     = this.querySelector('[data-slider-prev]');
    this.nextBtn     = this.querySelector('[data-slider-next]');
    this.dotsWrap    = this.querySelector('[data-slider-dots]');
    this.autoplay    = this.dataset.autoplay === 'true';
    this.speed       = parseInt(this.dataset.speed, 10) || 5000;
    this.loop        = this.dataset.loop !== 'false';
    this.currentIdx  = 0;
    this.timer       = null;
    this._cloneFirst = null;
    this._cloneLast  = null;
    this._jumping    = false;

    // Capture real slides only (clones won't have data-slide)
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));

    if (!this.track || !this.slides.length) return;

    if (this.loop && this.slides.length > 1) {
      this._setupInfiniteClones();
    }

    this.buildDots();
    this.bindEvents();
    this.updateNav();

    if (this.autoplay) this.startAutoplay();

    requestAnimationFrame(() => this._checkNavNeeded());
    this._ro = new ResizeObserver(() => this._checkNavNeeded());
    this._ro.observe(this.track);
  }

  // aria-hidden clones must not contain tabbable elements
  _removeCloneFocusables(clone) {
    clone
      .querySelectorAll('a[href], button, input, select, textarea, [tabindex]')
      .forEach((el) => el.setAttribute('tabindex', '-1'));
  }

  // Prepend clone of last slide + append clone of first slide for seamless wrapping
  _setupInfiniteClones() {
    this._cloneLast = this.slides[this.slides.length - 1].cloneNode(true);
    this._cloneLast.setAttribute('aria-hidden', 'true');
    this._cloneLast.removeAttribute('data-slide');
    this._cloneLast.classList.add('slide--clone');
    this._removeCloneFocusables(this._cloneLast);
    this.track.insertBefore(this._cloneLast, this.track.firstChild);

    this._cloneFirst = this.slides[0].cloneNode(true);
    this._cloneFirst.setAttribute('aria-hidden', 'true');
    this._cloneFirst.removeAttribute('data-slide');
    this._cloneFirst.classList.add('slide--clone');
    this._removeCloneFocusables(this._cloneFirst);
    this.track.appendChild(this._cloneFirst);

    // Scroll past the prepended clone to land on the real first slide
    requestAnimationFrame(() => {
      this.track.scrollLeft = this.slides[0].offsetLeft - this.track.offsetLeft;
    });
  }

  buildDots() {
    if (!this.dotsWrap || this.slides.length <= 1) return;
    this.dotsWrap.innerHTML = '';

    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.className = 'slider-dot' + (i === 0 ? ' is-active' : '');
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsWrap.appendChild(dot);
    });
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });

    if (this.autoplay) {
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin',    () => this.stopAutoplay());
      this.addEventListener('focusout',   () => this.startAutoplay());
    }

    if (this.track) {
      this.track.addEventListener('scrollend', () => this.syncFromScroll(), { passive: true });
      this.track.addEventListener('scroll', () => {
        clearTimeout(this._scrollTimer);
        this._scrollTimer = setTimeout(() => this.syncFromScroll(), 120);
      }, { passive: true });
    }

    let touchStartX = 0;
    this.track?.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    this.track?.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? this.next() : this.prev();
      }
    }, { passive: true });
  }

  goTo(index, behavior = 'smooth') {
    if (!this.track) return;
    const slide = this.slides[index];
    if (!slide) return;
    const left = slide.offsetLeft - this.track.offsetLeft;
    this.track.scrollTo({ left, behavior });
    this.currentIdx = index;
    this.updateDots();
    this.updateNav();
  }

  next() {
    if (this.loop && this.slides.length > 1 && this.currentIdx === this.slides.length - 1) {
      // Animate to the appended clone of the first slide, then silently jump back
      if (this._cloneFirst) {
        this._jumping = true;
        this.track.scrollTo({ left: this._cloneFirst.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
        setTimeout(() => {
          this.track.scrollLeft = this.slides[0].offsetLeft - this.track.offsetLeft;
          this.currentIdx = 0;
          this.updateDots();
          this.updateNav();
          this._jumping = false;
        }, 400);
      }
    } else {
      const nextIdx = this.loop
        ? (this.currentIdx + 1) % this.slides.length
        : Math.min(this.currentIdx + 1, this.slides.length - 1);
      this.goTo(nextIdx);
    }
  }

  prev() {
    if (this.loop && this.slides.length > 1 && this.currentIdx === 0) {
      // Animate to the prepended clone of the last slide, then silently jump forward
      if (this._cloneLast) {
        this._jumping = true;
        this.track.scrollTo({ left: this._cloneLast.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
        setTimeout(() => {
          this.track.scrollLeft = this.slides[this.slides.length - 1].offsetLeft - this.track.offsetLeft;
          this.currentIdx = this.slides.length - 1;
          this.updateDots();
          this.updateNav();
          this._jumping = false;
        }, 400);
      }
    } else {
      const prevIdx = this.loop
        ? (this.currentIdx - 1 + this.slides.length) % this.slides.length
        : Math.max(this.currentIdx - 1, 0);
      this.goTo(prevIdx);
    }
  }

  syncFromScroll() {
    if (!this.track || this._jumping) return;
    const scrollLeft = this.track.scrollLeft;

    // If user manually swiped into a clone, silently teleport to the real counterpart
    if (this.loop && this.slides.length > 1) {
      if (this._cloneLast && Math.abs(scrollLeft - (this._cloneLast.offsetLeft - this.track.offsetLeft)) < 10) {
        this.track.scrollLeft = this.slides[this.slides.length - 1].offsetLeft - this.track.offsetLeft;
        this.currentIdx = this.slides.length - 1;
        this.updateDots();
        this.updateNav();
        return;
      }
      if (this._cloneFirst && Math.abs(scrollLeft - (this._cloneFirst.offsetLeft - this.track.offsetLeft)) < 10) {
        this.track.scrollLeft = this.slides[0].offsetLeft - this.track.offsetLeft;
        this.currentIdx = 0;
        this.updateDots();
        this.updateNav();
        return;
      }
    }

    let closestIdx = 0;
    let minDist = Infinity;
    this.slides.forEach((slide, i) => {
      const dist = Math.abs((slide.offsetLeft - this.track.offsetLeft) - scrollLeft);
      if (dist < minDist) { minDist = dist; closestIdx = i; }
    });

    this.currentIdx = closestIdx;
    this.updateDots();
    this.updateNav();
  }

  updateDots() {
    this.dotsWrap?.querySelectorAll('.slider-dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIdx);
    });
  }

  updateNav() {
    if (this.loop) return;
    // Determine the ends from the actual scroll position, not the slide index.
    // With multi-column layouts the last slide's index is never the aligned
    // snap position (the track maxes out while an earlier slide is closest),
    // so an index check would leave "next" enabled at the true end.
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = maxScroll <= 2 || this.track.scrollLeft >= maxScroll - 2;

    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', atStart);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', atEnd);
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stopAutoplay() {
    clearInterval(this.timer);
    this.timer = null;
  }

  _checkNavNeeded() {
    if (!this.track) return;
    // Prefer slide-count over scroll dimension measurement — Safari can report
    // scrollWidth === clientWidth on scroll-snap containers even with multiple slides.
    const multiSlide = this.slides.length > 1;
    const needsNav = multiSlide || this.track.scrollWidth > this.track.clientWidth + 2;
    const hidden = !needsNav;
    if (this.prevBtn) this.prevBtn.hidden = hidden;
    if (this.nextBtn) this.nextBtn.hidden = hidden;
    if (this.dotsWrap) this.dotsWrap.hidden = hidden;
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this._ro?.disconnect();
  }
}

customElements.define('flc-slider', FLCSlider);
